import api from '../api/client';
import React from 'react';
import { Card, Row, Col, Tag, Progress, Typography, Statistic } from 'antd';
import {
    HeatOutlined,
    ColdOutlined,
    HomeOutlined,
    DashboardOutlined,
    AlertOutlined,
    CheckCircleOutlined,
    HeartFilled,
    CloudDownloadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const AnimalInsights = ({ insights, animalType, animalCount }) => {
    if (!insights) return null;

    const getRiskColor = (risk) => {
        switch (risk) {
            case 'high':
                return '#cf1322';
            case 'medium':
                return '#faad14';
            case 'low':
                return '#52c41a';
            default:
                return '#d9d9d9';
        }
    };

    const getAnimalIcon = (type) => {
        const icons = {
            cattle: '🐄',
            poultry: '🐔',
            swine: '🐖',
            sheep: '🐑',
            general: '🐄'
        };
        return icons[type] || icons.general;
    };

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '20px', marginRight: '8px' }}>{getAnimalIcon(animalType)}</span>
                    Animal Farming Insights ({animalType?.toUpperCase() || 'GENERAL'})
                </div>
            }
            bordered={false}
        >
            <Row gutter={[16, 16]}>
                {/* Heat Stress Risk */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Heat Stress Risk">
                        <div style={{ textAlign: 'center' }}>
                            <HeartFilled
                                style={{
                                    fontSize: '24px',
                                    color: getRiskColor(insights.heatStressRisk.risk)
                                }}
                            />
                            <Tag color={getRiskColor(insights.heatStressRisk.risk)} style={{ margin: '8px 0', width: '100%' }}>
                                {insights.heatStressRisk.risk?.toUpperCase()}
                            </Tag>
                            <Text type="secondary">{insights.heatStressRisk.advice}</Text>
                        </div>
                    </Card>
                </Col>

                {/* Cold Stress Risk */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Cold Stress Risk">
                        <div style={{ textAlign: 'center' }}>
                            <CloudDownloadOutlined
                                style={{
                                    fontSize: '24px',
                                    color: getRiskColor(insights.coldStressRisk.risk)
                                }}
                            />
                            <Tag color={getRiskColor(insights.coldStressRisk.risk)} style={{ margin: '8px 0', width: '100%' }}>
                                {insights.coldStressRisk.risk?.toUpperCase()}
                            </Tag>
                            <Text type="secondary">{insights.coldStressRisk.advice}</Text>
                        </div>
                    </Card>
                </Col>

                {/* Grazing Conditions */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Grazing Conditions">
                        <div style={{ textAlign: 'center' }}>
                            {insights.grazingConditions.suitable ? (
                                <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                            ) : (
                                <AlertOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                            )}
                            <div style={{ marginTop: '8px' }}>
                                <Text strong>{insights.grazingConditions.suitable ? 'SUITABLE' : 'LIMITED'}</Text>
                            </div>
                            <Text type="secondary">{insights.grazingConditions.advice}</Text>
                        </div>
                    </Card>
                </Col>

                {/* Shelter Advice */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Shelter Requirements">
                        <div style={{ textAlign: 'center' }}>
                            <HomeOutlined
                                style={{
                                    fontSize: '24px',
                                    color: insights.shelterAdvice.needed ? '#faad14' : '#52c41a'
                                }}
                            />
                            <div style={{ marginTop: '8px' }}>
                                <Text strong>{insights.shelterAdvice.needed ? 'SHELTER NEEDED' : 'SHELTER OK'}</Text>
                            </div>
                            <Text type="secondary">{insights.shelterAdvice.advice}</Text>
                        </div>
                    </Card>
                </Col>

                {/* Health Risk */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Health Risk Assessment">
                        <div style={{ textAlign: 'center' }}>
                            <Tag
                                color={getRiskColor(insights.healthRisk.risk)}
                                style={{ fontSize: '14px', padding: '8px 16px', marginBottom: '8px' }}
                            >
                                {insights.healthRisk.risk?.toUpperCase()} RISK
                            </Tag>
                            <div>
                                {insights.healthRisk.conditions.map((condition, index) => (
                                    <Text key={index} type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                                        • {condition}
                                    </Text>
                                ))}
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Productivity Impact */}
                <Col xs={24} md={8}>
                    <Card size="small" title="Productivity Impact">
                        <div style={{ textAlign: 'center' }}>
                            <Statistic
                                value={insights.productivityImpact.impact}
                                valueStyle={{
                                    color: insights.productivityImpact.impact === 'reduced' ? '#cf1322' : '#52c41a',
                                    fontSize: '16px'
                                }}
                            />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {insights.productivityImpact.explanation}
                            </Text>
                        </div>
                    </Card>
                </Col>

                {/* Breeding Conditions */}
                <Col xs={24} md={12}>
                    <Card size="small" title="Breeding Conditions">
                        <div style={{ textAlign: 'center' }}>
                            {insights.breedingConditions.suitable ? (
                                <CheckCircleOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                            ) : (
                                <AlertOutlined style={{ fontSize: '20px', color: '#faad14' }} />
                            )}
                            <Text style={{ marginLeft: '8px' }}>{insights.breedingConditions.advice}</Text>
                        </div>
                    </Card>
                </Col>

                {/* Feeding Recommendations */}
                <Col xs={24} md={12}>
                    <Card size="small" title="Feeding Recommendations">
                        <div style={{ textAlign: 'center' }}>
                            <Text>{insights.feedingRecommendations}</Text>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default AnimalInsights;
